import React, { forwardRef } from 'react';
import MuiCard from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import CardMedia from '@mui/material/CardMedia';
import CardActionArea from '@mui/material/CardActionArea';
import CardHeader from '@mui/material/CardHeader';
import Box from '@mui/material/Box';
import { useTranslation } from 'next-i18next';

import MediaCarousel from 'src/components/ui/MediaCarousel';
import Typography from 'src/components/ui/Typography';
import AspectRatio from 'src/components/ui/AspectRatio';
import PriceEvaluationTag from 'src/components/ui/PriceEvaluationTag';
import Tag from 'src/components/ui/Tag';
import Avatar from 'src/components/ui/Avatar';
import ToggleItemInGallery from 'src/components/ui/ToggleItemInGallery';
import { colours } from 'src/constants/colours';
import { noImagePath } from 'src/constants/defaultImages';
import useUser from 'src/hooks/useUser';
import getOwnerLink from 'src/utils/getOwnerLink';
import useActivityToDisplay, { useCompanyActivityToDisplay } from 'src/hooks/useActivityToDisplay';
import formatPrice from 'src/utils/formatPrice';
import type { Ad, Medium, Owner } from 'src/types/__generated__/graphql';
import { AdStatuses } from 'src/types/__generated__/graphql';
import { routePathUser } from 'src/constants/router';
import { styledComponent, styledTag } from 'src/utils/styled';
import formatDistanceToNowStrict from 'src/utils/formatDistanceToNowStrict';
import TooltipButton from 'src/components/ui/TooltipButton';
import BlueLink from 'src/components/ui/BlueLink';
import HomeAttributeIconBar, { HomeAttributeIconBarVariants } from 'src/components/ad/HomeAttributeIconBar';
import { AnalyticsContextProvider } from 'src/hooks/useDynamicLogEvents';
import { EventParamKeys } from 'src/utils/analytics';
import type { icons } from '../ui/Icon';
import getPublisherDetails from 'src/utils/meloUtils';
import type { PropertyCardLinkProps } from './PropertyCardLink';
import PropertyCardLink from './PropertyCardLink';

const TagContainer = styledComponent(Box)`
    position: absolute;
    top: 0.5rem;
    left: 0.5rem;
    z-index: 10;
    > div {
        margin-bottom: 0.125rem;
    }
`;

const StyledCard = styledComponent(MuiCard)<{
    $hasHoverBoxShadow?: boolean;
}>`
    position: relative;
    background-color: ${colours.grey.G100};

    &.MuiPaper-rounded { border-radius: 1rem; }

    &:hover {
        ${({ $hasHoverBoxShadow }) =>
            $hasHoverBoxShadow ? 'box-shadow: none' : `box-shadow: 0 0.25rem 2rem -0.5rem ${colours.blackOpacity.O16}`};
    }
`;

const StyledCardHeader = styledComponent(CardHeader)`
    position: relative;
    .MuiCardHeader-avatar {
        margin-right: 0.25rem;
    }
    .MuiCardHeader-action {
        margin-top: 0;
        margin-right: 0;
        align-self: center;
    }
    .MuiCardHeader-content {
        max-width: calc(100% - 2rem);
    }
    padding: 0.25rem 1rem 1rem 1rem;
    width: 100%;
`;

const StyledCardContent = styledComponent(CardContent)<{ $isDarkCard: boolean }>`
    ${({ $isDarkCard }) =>
        $isDarkCard ? `background-color: ${colours.white.O04};` : `background-color: ${colours.white.main};`};
    padding: 0.25rem 1rem 0 1rem;

    &&:last-child {
        padding-bottom: 0;
    }
`;

const StyledCardActionArea = styledComponent(CardActionArea)<{ $isDarkCard: boolean }>`
    ${({ $isDarkCard }) =>
        $isDarkCard ? `background-color: ${colours.white.O04};` : `background-color: ${colours.white.main};`};
    .MuiCardActionArea-focusHighlight {
        background: transparent;
    }
`;

const PriceText = styledComponent(Typography)`
    font-size: 1.125rem;
    font-weight: bold;
`;

const StyledBlueLink = styledComponent(BlueLink)`
    line-height: 1rem;
    color: ${colours.black.O72};
`;

interface MediaCarouselWrapperProps {
    $status?: AdStatuses | null;
}

const MediaCarouselWrapper = styledTag('div')`
    .slide {
        & > * {
            display: flex;
        }
    }
    ${(props: MediaCarouselWrapperProps) =>
        props.$status !== AdStatuses.Available &&
        `
        opacity: 0.5;
    `}
`;

export type Props = {
    ad: Ad;
    showTags?: boolean;
    showUser?: boolean;
    onMouseEnter?: () => void;
    onMouseLeave?: () => void;
    isDarkCard?: boolean;
    hideAddToGalleriesButton?: boolean;
    hasHoverBoxShadow?: boolean;
    scrollIntoView?: boolean;
    buildAdHref?: PropertyCardLinkProps['buildAdHref'];
};

const PropertyCard: React.ForwardRefExoticComponent<Props & React.RefAttributes<HTMLDivElement>> = forwardRef<
    HTMLDivElement,
    Props
>(
    (
        {
            ad,
            showTags,
            showUser,
            onMouseEnter,
            onMouseLeave,
            isDarkCard = false,
            buildAdHref,
            hideAddToGalleriesButton = false,
            hasHoverBoxShadow,
        },
        ref,
    ) => {
        const { t, i18n } = useTranslation(['common', 'ad']);
        const user = useUser();
        const getCompanyActivityToDisplay = useCompanyActivityToDisplay();
        const getActivityToDisplay = useActivityToDisplay();
        const price = Number(ad.type === 'rent' ? ad.rent : ad.price);

        const ownerLink = getOwnerLink(ad.owner as Owner);

        const isCompany = Boolean(ad.owner?.company?.id) && Boolean(!ad.assignedTo);

        let title = '';
        let iconName: keyof typeof icons | undefined;
        const publisher = ad.mainPublisher && getPublisherDetails(ad.mainPublisher);
        if (showUser) {
            if (ad.assignedTo) {
                title = getActivityToDisplay(ad.assignedTo);
            } else if (ad.owner) {
                if (ad.owner.company) {
                    title = getCompanyActivityToDisplay(ad.owner.company);
                } else if (ad.owner.user) {
                    title = getActivityToDisplay(ad.owner.user);
                }
            } else if (publisher) {
                title = publisher.name;
                iconName = publisher.logo;
            } else {
                title = '';
            }
        }
        return (
            <AnalyticsContextProvider
                value={{
                    [EventParamKeys.ItemId]: ad.id,
                    [EventParamKeys.ItemName]: ad.title ?? undefined,
                    [EventParamKeys.PropertyType]: ad.propertyType,
                }}
            >
                <StyledCard
                    ref={ref}
                    elevation={0}
                    onMouseEnter={onMouseEnter}
                    onMouseLeave={onMouseLeave}
                    $hasHoverBoxShadow={hasHoverBoxShadow}
                >
                    <PropertyCardLink ad={ad} buildAdHref={buildAdHref}>
                        <CardMedia>
                            {showTags && (
                                <TagContainer>
                                    {Boolean(user?.isRealEstatesTrades && ad.interOffice) && (
                                        <Box mb={1}>
                                            <Tag
                                                backgroundColor={colours.secondaryOpaque.O12}
                                                textColor={colours.secondary.main}
                                                label={t('ad:interOffice')}
                                                size="tiny"
                                            />
                                        </Box>
                                    )}
                                    {ad.type === 'sell' && ad.price && ad.homadataEvaluation && (
                                        <PriceEvaluationTag
                                            price={ad.price}
                                            confidenceMin={ad.homadataEvaluation.confidenceMin}
                                            confidenceMax={ad.homadataEvaluation.confidenceMax}
                                            size="tiny"
                                        />
                                    )}
                                    {ad.isNew && (
                                        <Box mb={2}>
                                            <Tag
                                                backgroundColor={colours.tertiaryOpaque.O12}
                                                textColor={colours.tertiary.main}
                                                label={t('ad:new')}
                                                size="tiny"
                                            />
                                        </Box>
                                    )}
                                    {ad.isPriceDecreasing && (
                                        <Box mb={2}>
                                            <Tag
                                                size="small"
                                                label={t('ad:decreasingPrice')}
                                                backgroundColor={colours.successOpaque.O12}
                                                textColor={colours.success.main}
                                            />
                                        </Box>
                                    )}
                                    {ad.status === 'ended' && (
                                        <Box mb={2}>
                                            <Tag
                                                backgroundColor={colours.tertiaryOpaque.O12}
                                                textColor={colours.tertiary.main}
                                                label={t(`ad:${ad.type === 'sell' ? 'soldLabel' : 'rentedLabel'}`)}
                                                size="tiny"
                                            />
                                        </Box>
                                    )}
                                </TagContainer>
                            )}
                            <MediaCarouselWrapper $status={ad.status}>
                                <AspectRatio ratio={1.114}>
                                    <MediaCarousel
                                        media={ad.media.length ? ad.media : [noImagePath]}
                                        alt={ad.title as string}
                                        zoomableImages
                                    />
                                </AspectRatio>
                            </MediaCarouselWrapper>
                        </CardMedia>
                        <StyledCardContent $isDarkCard={isDarkCard}>
                            <Box
                                display="flex"
                                flexDirection="row"
                                justifyContent="space-between"
                                mb="-0.5rem"
                                mt={hideAddToGalleriesButton ? '0.5rem' : 0}
                            >
                                <Box display="flex" alignItems="center" mb={hideAddToGalleriesButton ? '0.75rem' : 0}>
                                    <PriceText data-target="card-price" data-target-id="card-price" variant="h4">
                                        {formatPrice({
                                            price,
                                            lang: i18n.language,
                                        })}
                                    </PriceText>
                                </Box>
                                {!hideAddToGalleriesButton && (
                                    <Box
                                        onClick={(e) => {
                                            e.stopPropagation();
                                        }}
                                        padding="0"
                                        mr="-0.5rem"
                                    >
                                        <TooltipButton
                                            title={
                                                ad.isUserFavorite
                                                    ? t('common:tooltips.removeFromGalleries')
                                                    : t('common:tooltips.addToGalleries')
                                            }
                                            ButtonComponent={ToggleItemInGallery}
                                            buttonProps={{
                                                ad,
                                            }}
                                            isControlled
                                        />
                                    </Box>
                                )}
                            </Box>
                            <Typography
                                data-target="card-address"
                                data-target-id="card-address"
                                variant="body2"
                                color="textSecondary"
                            >{`${ad.address.city?.name ?? ad.address.cityName}${
                                ad.address.zipCode ? `, ${ad.address.zipCode} ` : ''
                            }`}</Typography>
                            <Box display="flex" alignItems="center" pb={showUser ? 0 : '1rem'}>
                                <Typography variant="body2" color="textSecondary" noWrap>
                                    {t(`ad:propertyTypes.${ad.propertyType}`)}
                                </Typography>
                                <Typography pl={0.5} pr={0.5}>
                                    ·
                                </Typography>
                                <HomeAttributeIconBar
                                    ad={ad}
                                    homePropertiesDensity={HomeAttributeIconBarVariants.MEDIUM}
                                />
                            </Box>
                        </StyledCardContent>
                        {showUser && (
                            <StyledCardActionArea $isDarkCard={isDarkCard} disableRipple>
                                <Box display="flex" alignItems="center">
                                    <StyledCardHeader
                                        avatar={
                                            <Avatar
                                                data-target="card-author-avatar"
                                                data-target-id="card-author-avatar"
                                                alt={
                                                    ad.assignedTo?.fullName ??
                                                    ad.owner?.displayName ??
                                                    ad.mainPublisher?.name
                                                }
                                                image={(ad.assignedTo?.image ?? ad.owner?.image) as Medium}
                                                fallbackText={ad.assignedTo?.initials ?? ad.owner?.initials}
                                                isCompany={Boolean(isCompany || ad.mainPublisher)}
                                                isPublisher={Boolean(publisher)}
                                                size={16}
                                                iconName={iconName}
                                                iconColor="transparent"
                                            />
                                        }
                                        title={
                                            <Box display="flex" flexDirection="row">
                                                <Box overflow="hidden" textOverflow="ellipsis" whiteSpace="nowrap">
                                                    {ad.mainPublisher ? (
                                                        <Typography
                                                            variant="h6"
                                                            color={colours.black.O72}
                                                            weight="normal"
                                                        >
                                                            {title}
                                                        </Typography>
                                                    ) : (
                                                        <StyledBlueLink
                                                            data-target="card-author-name"
                                                            data-target-id="card-author-name"
                                                            href={
                                                                ad.assignedTo
                                                                    ? `${routePathUser}/${ad.assignedTo.id}`
                                                                    : ownerLink
                                                            }
                                                        >
                                                            {title}
                                                        </StyledBlueLink>
                                                    )}
                                                </Box>
                                                <Box display="flex" flex="1" minWidth="fit-content" alignItems="center">
                                                    <Typography
                                                        variant="h6"
                                                        fontWeight={400}
                                                        color={colours.black.O72}
                                                        lineHeight="1rem"
                                                    >
                                                        &nbsp;
                                                        {`· ${t('ad:since', {
                                                            momentString: formatDistanceToNowStrict(
                                                                ad.createdAt as string,
                                                                i18n.language as SupportedLangs,
                                                            ),
                                                        })}`}
                                                    </Typography>
                                                </Box>
                                            </Box>
                                        }
                                    />
                                </Box>
                            </StyledCardActionArea>
                        )}
                    </PropertyCardLink>
                </StyledCard>
            </AnalyticsContextProvider>
        );
    },
);

export default PropertyCard;
